import React from 'react';
import { ArrowLeftIcon, ArrowRightIcon, HomeIcon, MapIcon } from 'lucide-react';
import { disclaimer } from '../data/teaser';
import { brandLogo } from '../data/journey';
import type { BrandLevel } from '../data/journey';
interface PhoneFrameProps {
  step: number;
  total: number;
  children: React.ReactNode;
  footnote?: string;
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  /** Opens the internal strategy document. */
  onOpenStrategy: () => void;
  /** Opens the campaign journey visualisation. */
  onOpenJourney: () => void;
  /** Chapter label shown beside the screen counter, e.g. "Snak 2". */
  chapter?: string;
  railEyebrow?: string;
  /** Two-line rail headline. */
  railTitle?: string[];
  railBlurb?: string;
  railDisclaimer?: string;
  /** How much brand identity this phase may carry. */
  brand?: BrandLevel;
  /** Returns to the entry menu. Hidden on the menu itself. */
  onHome?: () => void;
  /** Label for the current mode, shown under the rail counter. */
  modeLabel?: string;
  /** Opens the printable storyboard. */
  onOpenStoryboard?: () => void;
  /** Render only the screen, filling its parent (e.g. inside a device shell). */
  embedded?: boolean;
}
export function PhoneFrame({
  step,
  total,
  children,
  footnote,
  onBack,
  onNext,
  nextLabel = 'Continue',
  nextDisabled = false,
  onOpenStrategy,
  onOpenJourney,
  chapter,
  railEyebrow = 'Unbranded disease awareness · medical retina',
  railTitle = ['See beyond', 'the fluid.'],
  railBlurb = 'A 60-second teaser for Danish retina specialists on DME phenotypes and AI-assisted OCT biomarker analysis.',
  railDisclaimer = disclaimer,
  brand = 'none',
  onHome,
  modeLabel,
  onOpenStoryboard,
  embedded = false
}: PhoneFrameProps) {
  const isDark = brand === 'full';
  const surface = isDark ? 'border-ozNavy bg-ozNavy' : brand === 'colour' ? 'border-line bg-ozMist/50' : 'border-line bg-paper';
  const screen = <div className={`flex flex-col overflow-hidden ${embedded ? `h-full w-full ${isDark ? 'bg-ozNavy' : brand === 'colour' ? 'bg-ozMist/50' : 'bg-paper'}` : `aspect-[9/16] w-full rounded-[34px] border shadow-soft ${surface}`}`} data-id="element-410" data-mp="23xv1mt0i8wai">
      {brand !== 'none' && <div className={`h-1 w-full shrink-0 ${isDark ? 'bg-ozGreen' : 'bg-ozBlue'}`} aria-hidden="true" data-id="element-411" data-mp="1yyrxrbw9gw44" />}

      <div className={`flex items-center justify-between gap-3 px-6 ${embedded ? 'pt-9' : 'pt-5'}`} data-id="element-412" data-mp="1l6lf9j5myd1i">
        <div className="flex min-w-0 items-center gap-2" data-id="element-413" data-mp="1pyvc7j564dda">
          {onHome && <button type="button" onClick={onHome} aria-label="Back to the entry menu" className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-colors duration-150 focus:outline-none focus-visible:ring-2 ${isDark ? 'border-paper/25 text-paper/70 hover:border-ozGreen hover:text-paper focus-visible:ring-ozGreen' : 'border-line text-mut hover:border-mut hover:text-navy focus-visible:ring-blue'}`} data-id="element-414" data-mp="1ba8n4ppqo78v">
              <HomeIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-415" data-mp="wm8ncy1934g4" />
            </button>}
          {isDark ? <span className="rounded bg-paper px-2 py-1" data-id="element-416" data-mp="skna3bkt0o5e">
              <img src={brandLogo.src} alt={brandLogo.alt} draggable={false} className="h-5 w-auto select-none object-contain object-left" data-id="element-417" data-mp="sn9gv8zvezmk" />
            </span> : chapter && <span className="truncate text-[9px] uppercase tracking-micro text-mut" data-id="element-418" data-mp="1gckcyd6a05ud">{chapter}</span>}
        </div>

        <div className="flex shrink-0 items-center gap-1.5" data-id="element-419" data-mp="1pamkzcoffyr5">
          <button type="button" onClick={onOpenJourney} aria-label="See the campaign journey" className={`flex h-7 w-7 items-center justify-center rounded-full border transition-colors duration-150 focus:outline-none focus-visible:ring-2 ${isDark ? 'border-ozGreen/60 text-ozGreen hover:bg-ozGreen/15 focus-visible:ring-ozGreen' : 'border-blue/60 bg-paper text-navy hover:border-blue hover:bg-sky/50 focus-visible:ring-blue'}`} data-id="element-420" data-mp="1p934bkyuzlou">
            <MapIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-421" data-mp="2vu76noj6xn3y" />
          </button>
          <button type="button" onClick={onOpenStrategy} className={`rounded-full border px-3 py-1 text-[10.5px] font-medium transition-colors duration-150 focus:outline-none focus-visible:ring-2 ${isDark ? 'border-ozGreen/60 text-paper hover:bg-ozGreen/15 focus-visible:ring-ozGreen' : 'border-blue/60 bg-paper text-navy hover:border-blue hover:bg-sky/50 focus-visible:ring-blue'}`} data-id="element-422" data-mp="30s8rof06fwi2">
            Strategy
          </button>
        </div>
      </div>

      <div className="no-scrollbar flex flex-1 flex-col overflow-y-auto px-6 pb-5 pt-3" data-id="element-423" data-mp="z64kzjxx8oww">{children}</div>

      {(onNext || onBack || footnote) && <div className={`border-t px-6 py-4 ${isDark ? 'border-paper/12' : 'border-line'} ${embedded ? 'pb-6' : ''}`} data-id="element-424" data-mp="pgl0yayh2p0g">
          {footnote && <p className={`mb-3 text-[9px] leading-relaxed ${isDark ? 'text-paper/45' : 'text-mut'}`} data-id="element-425" data-mp="lf9bq6jig5ss">
              {footnote}
            </p>}
          {(onNext || onBack) && <div className="flex items-center gap-2" data-id="element-426" data-mp="bfqvo16q1460">
              {onBack && <button type="button" onClick={onBack} aria-label="Previous screen" className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full border transition-colors duration-150 focus:outline-none focus-visible:ring-2 ${isDark ? 'border-paper/25 text-paper/70 hover:border-ozGreen hover:text-paper focus-visible:ring-ozGreen' : 'border-line text-mut hover:border-mut hover:text-navy focus-visible:ring-blue'}`} data-id="element-427" data-mp="6j0d977g1whf">
                  <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-428" data-mp="3hea4ztcjv7td" />
                </button>}
              {onNext && <button type="button" onClick={onNext} disabled={nextDisabled} className={`flex h-11 flex-1 items-center justify-center gap-2 rounded-full px-5 text-[13px] font-semibold transition-opacity duration-150 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-30 ${isDark ? 'bg-ozGreen text-ozNavy focus-visible:ring-ozGreen focus-visible:ring-offset-ozNavy' : 'bg-navy text-paper focus-visible:ring-blue focus-visible:ring-offset-2'}`} data-id="element-429" data-mp="cyhbvonaphvi">
                  {nextLabel}
                  <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-430" data-mp="2h4itp2f23dsh" />
                </button>}
            </div>}
        </div>}
    </div>;
  if (embedded) return screen;
  return <div className="flex min-h-full w-full items-center justify-center bg-mist px-5 py-8" data-id="element-431" data-mp="2q4xhc8fht4z5">
      <div className="flex w-full max-w-5xl items-center justify-center gap-16" data-id="element-432" data-mp="22f34hqv6bgew">
        <aside className="hidden max-w-xs flex-1 lg:block" data-id="element-433" data-mp="1xgjkhw054ep6">
          <p className="text-[10px] uppercase tracking-micro text-mut" data-id="element-434" data-mp="1xi49z77ll7g6">{railEyebrow}</p>
          <h1 className="mt-5 font-display text-4xl font-semibold leading-[1.05] text-navy" data-id="element-435" data-mp="2c67jurorh3a9">
            {railTitle[0]}
            <br data-id="element-436" data-mp="27debzuxzxety" />
            {railTitle[1]}
          </h1>
          <p className="mt-5 text-sm leading-relaxed text-mut" data-id="element-437" data-mp="1jod5id8q4drb">{railBlurb}</p>
          {total > 0 && <p className="mt-6 text-[11px] uppercase tracking-micro text-mut" data-id="element-438" data-mp="1ep0inshur6oq">
              {chapter ? `${chapter} · ` : ''}Screen {step + 1} of {total}
            </p>}
          {modeLabel && <p className="mt-1.5 text-[11px] uppercase tracking-micro text-mut" data-id="element-439" data-mp="y6laolikuxa4">{modeLabel}</p>}
          {onOpenStoryboard && <button type="button" onClick={onOpenStoryboard} className="mt-5 rounded-full border border-line px-3.5 py-1.5 text-[11px] font-medium text-navy transition-colors duration-150 hover:border-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-440" data-mp="13798bq1bacdb">
              Storyboard (PDF)
            </button>}
          <p className="mt-6 border-t border-line pt-4 text-[11px] leading-relaxed text-mut" data-id="element-441" data-mp="1lxh75vxzagnp">
            {railDisclaimer}
          </p>
        </aside>

        <div className="flex w-full max-w-[400px] flex-col" data-id="element-442" data-mp="1vulnhf557hwl">{screen}</div>
      </div>
    </div>;
}